/**
 * Helper functions for API interactions
 */

/**
 * Get the base URL for a Netlify function based on the current environment
 * In development, we need to handle Netlify Functions differently
 */
export const getNetlifyFunctionUrl = (functionName: string): string => {
  // Local development using netlify dev
  if (process.env.NODE_ENV === 'development') {
    // Default Netlify Functions path
    return `/.netlify/functions/${functionName}`;
  }

  // Production environment (deployed on Netlify)
  return `/.netlify/functions/${functionName}`;
}

/**
 * Properly handles API responses to catch both server errors and application errors
 * @param response - Fetch API response object
 * @returns Promise that resolves to the JSON response or rejects with an error message
 */
export const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    // Try to get error details from the response if possible
    try {
      const errorData = await response.json();
      throw new Error(errorData.error || `Server error: ${response.status}`);
    } catch (e) {
      throw new Error(`Server error: ${response.status}`);
    }
  }

  // For successful responses, safely handle empty responses or JSON parsing errors
  try {
    return await response.json();
  } catch (e) {
    // Return a default success response if the server didn't return valid JSON
    // This can happen with 204 No Content responses
    return { success: true };
  }
} 